import IconInfoOutline from "@common/components/icons/IconInfoOutline";
import {
  Card,
  Flex,
  Stack,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { PropsWithChildren, ReactElement, useState } from "react";
import { useSelectCardStyles } from "./SelectCard.styled";
import { ISelectCardProps } from "./types";

export default function SelectCard({
  colorScheme,
  shouldTitleBeColored,
  icon,
  noGrow,
  title,
  tooltip,
  onClick,
  selected,
  disabled,
  disabledTooltip,
  isClickable = true,
  children,
}: PropsWithChildren<ISelectCardProps>): ReactElement {
  // toggle
  const [tooltipIsOpened, setTooltipIsOpened] = useState(false);

  // styles
  const theme = useMantineTheme();
  const { classes } = useSelectCardStyles({
    colorScheme,
    noGrow: noGrow || false,
    shouldTitleBeColored,
    disabled: disabled || false,
    hasDisabledTooltip: !!disabled && !!disabledTooltip,
  });

  return (
    <div className={classes.wrapper}>
      <Card
        data-testid={title.toLowerCase().split(" ").join("")}
        component={disabled ? "div" : "button"}
        type="button"
        className={classes.root}
        withBorder
        p="xs"
        onClick={disabled ? undefined : onClick}
        data-selected={selected && !disabled}
        data-clickable={isClickable}
        disabled={disabled}
        pos="relative"
      >
        <Stack spacing={theme.spacing.xs / 2}>
          {icon && (
            <Flex
              align="center"
              direction="row"
              justify="flex-start"
              h={20}
              w={20}
            >
              {icon}
            </Flex>
          )}
          <Title className={classes.title} order={3} size="h6" m={0}>
            {title}
          </Title>
          {children}
        </Stack>

        {disabled && (
          <Flex
            className={classes.disabledTooltip}
            align="center"
            direction="row"
            justify="center"
            bg="red.0"
            h="100%"
            left={0}
            p="xs"
            pos="absolute"
            ta="center"
            top={0}
            w="100%"
          >
            {disabledTooltip}
          </Flex>
        )}
      </Card>

      {tooltip && (
        <div className={classes.tooltip}>
          <Tooltip
            arrowSize={theme.spacing.xs}
            color="light"
            label={tooltip}
            multiline
            opened={tooltipIsOpened}
            position="left"
            withArrow
            withinPortal
            p={theme.spacing.sm}
          >
            <button
              className={classes.tooltipIcon}
              onClick={() => setTooltipIsOpened(!tooltipIsOpened)}
              onMouseOver={() => setTooltipIsOpened(true)}
              onMouseLeave={() => setTooltipIsOpened(false)}
            >
              <IconInfoOutline color={theme.colors.gray[4]} />
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
